import type { User } from '@supabase/supabase-js';
import { BadgeCheck, CheckCircle2, Eye, EyeOff, KeyRound, Link2, RefreshCw, ShieldCheck } from 'lucide-react';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { getAccountIdentityStatus, validatePasswordChange, validatePasswordVerificationCode } from '../lib/accountSecurity';
import { assertOnline, withTimeout } from '../lib/async';
import { friendlyAuthError } from '../lib/errors';
import { supabase } from '../lib/supabase';

interface AccountSecurityProps {
  user: User;
  onNotice: (message: string) => void;
}

export function AccountSecurity({ user, onNotice }: AccountSecurityProps) {
  const identityStatus = useMemo(() => getAccountIdentityStatus(user), [user]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [signOutOthers, setSignOutOthers] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [linkError, setLinkError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const passwordBusyRef = useRef(false);
  const linkBusyRef = useRef(false);

  const connectGoogle = async () => {
    if (!supabase || identityStatus.hasGoogle || linkBusyRef.current) return;
    linkBusyRef.current = true;
    setLinkBusy(true);
    setLinkError('');
    try {
      assertOnline();
      const redirectTo = `${window.location.origin}/?account=security`;
      const { error } = await withTimeout(supabase.auth.linkIdentity({ provider: 'google', options: { redirectTo } }), 15_000);
      if (error) throw error;
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'เชื่อมต่อ Google ไม่สำเร็จ';
      setLinkError(/manual_linking_disabled|manual linking/i.test(message)
        ? 'ต้องเปิด Manual identity linking ใน Supabase Auth ก่อนใช้งานปุ่มนี้'
        : friendlyAuthError(message));
      linkBusyRef.current = false;
      setLinkBusy(false);
    }
  };

  const requestPasswordVerification = async () => {
    if (!supabase || passwordBusyRef.current) return;
    const validationError = validatePasswordChange({ hasEmailPassword: identityStatus.hasEmailPassword, currentPassword, newPassword, confirmPassword });
    if (validationError) { setPasswordError(validationError); return; }
    if (!user.email) { setPasswordError('บัญชีนี้ไม่มีอีเมลสำหรับยืนยันตัวตน'); return; }

    passwordBusyRef.current = true;
    setPasswordBusy(true);
    setPasswordError('');
    setPasswordSuccess('');
    try {
      assertOnline();
      if (identityStatus.hasEmailPassword) {
        const { error: signInError } = await withTimeout(supabase.auth.signInWithPassword({ email: user.email, password: currentPassword }), 15_000);
        if (signInError) throw signInError;
      }
      const { error: reauthenticateError } = await withTimeout(supabase.auth.reauthenticate(), 15_000);
      if (reauthenticateError) throw reauthenticateError;
      setVerificationSent(true);
      setVerificationCode('');
      setPasswordSuccess(`ส่งรหัสยืนยันไปที่ ${user.email} แล้ว กรุณาตรวจกล่องขาเข้าและสแปม`);
    } catch (reason) {
      setPasswordError(friendlyAuthError(reason instanceof Error ? reason.message : 'ส่งรหัสยืนยันไม่สำเร็จ'));
    } finally {
      passwordBusyRef.current = false;
      setPasswordBusy(false);
    }
  };

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supabase || passwordBusyRef.current || !verificationSent) return;
    const validationError = validatePasswordChange({ hasEmailPassword: identityStatus.hasEmailPassword, currentPassword, newPassword, confirmPassword });
    if (validationError) { setPasswordError(validationError); return; }
    const verificationError = validatePasswordVerificationCode(verificationCode);
    if (verificationError) { setPasswordError(verificationError); return; }
    passwordBusyRef.current = true;
    setPasswordBusy(true);
    setPasswordError('');
    try {
      assertOnline();
      const { error: updateError } = await withTimeout(supabase.auth.updateUser({
        password: newPassword,
        nonce: verificationCode,
        ...(identityStatus.hasEmailPassword ? { current_password: currentPassword } : {}),
      }), 15_000);
      if (updateError) throw updateError;
      if (signOutOthers) {
        const { error: signOutError } = await withTimeout(supabase.auth.signOut({ scope: 'others' }), 10_000);
        if (signOutError) throw signOutError;
      }
      const message = identityStatus.hasEmailPassword ? 'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว' : 'ตั้งรหัสผ่านสำหรับเข้าสู่ระบบด้วยอีเมลแล้ว';
      setPasswordSuccess(message);
      onNotice(message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSignOutOthers(false);
      setVerificationSent(false);
      setVerificationCode('');
    } catch (reason) {
      setPasswordError(friendlyAuthError(reason instanceof Error ? reason.message : 'เปลี่ยนรหัสผ่านไม่สำเร็จ'));
    } finally {
      passwordBusyRef.current = false;
      setPasswordBusy(false);
    }
  };

  return <section className="content-card settings-card account-security-card">
    <div className="card-heading"><div className="setting-icon security"><ShieldCheck size={20} /></div><div><h2>บัญชีและความปลอดภัย</h2><p>จัดการวิธีเข้าสู่ระบบโดยไม่กระทบเอกสาร Free หรือข้อมูล Cloud</p></div></div>

    <div className="identity-list" aria-label="วิธีเข้าสู่ระบบ">
      <div className="identity-row"><div className="identity-mark email"><KeyRound size={17} /></div><div><strong>อีเมลและรหัสผ่าน</strong><span>{identityStatus.hasEmailPassword ? `เชื่อมกับ ${user.email ?? 'บัญชีนี้'}` : 'ยังไม่ได้ตั้งรหัสผ่านสำหรับบัญชีนี้'}</span></div><span className={`identity-state ${identityStatus.hasEmailPassword ? 'connected' : ''}`}>{identityStatus.hasEmailPassword ? <><BadgeCheck size={14} /> เชื่อมแล้ว</> : 'ยังไม่เชื่อม'}</span></div>
      <div className="identity-row"><div className="identity-mark google">G</div><div><strong>Google</strong><span>{identityStatus.hasGoogle ? `เชื่อมกับบัญชี BilaFlow เดิม (${user.email ?? 'อีเมลปัจจุบัน'})` : 'เชื่อม Google เพื่อเข้า user ID เดิมและเห็นข้อมูลชุดเดิม'}</span></div>{identityStatus.hasGoogle ? <span className="identity-state connected"><BadgeCheck size={14} /> เชื่อมแล้ว</span> : <button className="btn ghost small" type="button" onClick={() => void connectGoogle()} disabled={linkBusy}>{linkBusy ? <><RefreshCw className="spin" size={14} /> กำลังเปิด Google…</> : <><Link2 size={14} /> เชื่อมต่อ Google</>}</button>}</div>
    </div>
    {linkError && <div className="security-message error" role="alert">{linkError}</div>}
    <div className="security-boundary"><ShieldCheck size={15} /><span>การเชื่อมวิธีเข้าสู่ระบบเพิ่มจะไม่ย้าย ลบ หรือรวมข้อมูลจากบัญชีอื่น หาก Google ใช้อีเมลคนละบัญชี ระบบจะไม่เปลี่ยนเจ้าของเอกสารเดิม</span></div>

    <form className="password-change-form" onSubmit={changePassword}>
      <div className="password-form-heading"><div><strong>{identityStatus.hasEmailPassword ? 'เปลี่ยนรหัสผ่าน' : 'ตั้งรหัสผ่านสำหรับบัญชีนี้'}</strong><p>{identityStatus.hasEmailPassword ? 'ตรวจรหัสผ่านเดิมและยืนยันรหัส 8 หลักจากอีเมลก่อนบันทึก' : 'ยืนยันรหัส 8 หลักทางอีเมล แล้วคุณจะเข้าได้ทั้ง Google และอีเมล'}</p></div></div>
      {identityStatus.hasEmailPassword && <label><span>รหัสผ่านปัจจุบัน</span><input type={showPasswords ? 'text' : 'password'} autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} disabled={passwordBusy} /></label>}
      <div className="field-grid two"><label><span>รหัสผ่านใหม่</span><input type={showPasswords ? 'text' : 'password'} minLength={8} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} disabled={passwordBusy} /></label><label><span>ยืนยันรหัสผ่านใหม่</span><input type={showPasswords ? 'text' : 'password'} minLength={8} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} disabled={passwordBusy} /></label></div>
      <div className="password-options">
        <label><input type="checkbox" checked={showPasswords} onChange={(event) => setShowPasswords(event.target.checked)} disabled={passwordBusy} /><span>{showPasswords ? <EyeOff size={14} /> : <Eye size={14} />} แสดงรหัสผ่าน</span></label>
        <label><input type="checkbox" checked={signOutOthers} onChange={(event) => setSignOutOthers(event.target.checked)} disabled={passwordBusy} /><span>ออกจากระบบอุปกรณ์อื่นหลังเปลี่ยนสำเร็จ</span></label>
      </div>
      {verificationSent && <label className="password-verification"><span>รหัสยืนยัน 8 หลักจากอีเมล</span><input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{8}" maxLength={8} value={verificationCode} onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 8))} disabled={passwordBusy} placeholder="00000000" /><small>รหัสมีอายุจำกัด หากไม่ได้รับให้ตรวจ Spam หรือกดส่งใหม่</small></label>}
      {passwordError && <div className="security-message error" role="alert">{passwordError}</div>}
      {passwordSuccess && <div className="security-message success" role="status"><CheckCircle2 size={15} /> {passwordSuccess}</div>}
      {!verificationSent ? <button className="btn primary" type="button" onClick={() => void requestPasswordVerification()} disabled={passwordBusy}>{passwordBusy ? <><RefreshCw className="spin" size={15} /> กำลังส่งอีเมล…</> : <><KeyRound size={15} /> ส่งรหัสยืนยันทางอีเมล</>}</button> : <div className="password-verification-actions"><button className="btn ghost" type="button" onClick={() => void requestPasswordVerification()} disabled={passwordBusy}>ส่งรหัสใหม่</button><button className="btn primary" type="submit" disabled={passwordBusy || verificationCode.length !== 8}>{passwordBusy ? <><RefreshCw className="spin" size={15} /> กำลังบันทึก…</> : <><KeyRound size={15} /> ยืนยันและ{identityStatus.hasEmailPassword ? 'เปลี่ยนรหัสผ่าน' : 'ตั้งรหัสผ่าน'}</>}</button></div>}
    </form>
  </section>;
}

